import {Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, Output, PLATFORM_ID, SimpleChanges} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {ModalComponent} from '../../../../core/components/modal/modal.component';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {ToggleComponent} from '../../../../core/components/toggle/toggle.component';
import {HelperBannerComponent} from '../../../../core/components/helper-banner/helper-banner.component';
import {ServiceService} from '../../../services/service/service.service';
import {Service} from '../../../../core/entities/service/service.entity';
import {ToastrService} from 'ngx-toastr';
import {ImageUploadComponent} from '../../../../core/components/image-upload/image-upload.component';
import {isPlatformBrowser} from '@angular/common';
import {ServicePhoto} from '../../../../core/entities/service/service-photo.entity';
import {ServicePhotoService} from '../../../services/service/service-photo.service';
import {AppImageComponent} from '../../../../core/components/image/image';
import {CdkDragDrop, DragDropModule, moveItemInArray} from '@angular/cdk/drag-drop';
import {PreparedBatch} from '../../../../core/components/image-upload/image-upload.component';
import {Subject, Subscription, debounceTime} from 'rxjs';


@Component({
  selector: 'app-create-service-modal',
  standalone: true,
  imports: [
    ModalComponent,
    TranslocoPipe,
    ReactiveFormsModule,
    ToggleComponent,
    HelperBannerComponent,
    ImageUploadComponent,
    AppImageComponent,
    DragDropModule
],
  templateUrl: './create-service-modal.html',
  styleUrl: './create-service-modal.scss'
})
export class CreateServiceModal implements OnChanges, OnDestroy {
  @Input() open = false;
  @Input() service: Service|null = null;
  @Output() closed = new EventEmitter<Service|null>();
  @Output() deleted = new EventEmitter<Service|null>();

  public form: FormGroup;
  public uploadedImage: File | null = null;
  public mainImagePreviewUrl: string | null = null;
  public galleryPhotos: ServicePhoto[] = [];
  public queuedPhotos: { file: File; previewUrl: string }[] = [];
  public maxGalleryPhotos = 10;
  public creating = false;
  public editing = false;
  public sectionOpen = {
    info: true,
    photos: true,
    pricing: true,
    danger: true,
  };
  private orderChanged$ = new Subject<void>();
  private orderSub?: Subscription;
  private readonly defaultFormValues = {
    name: '',
    description: '',
    duration: 60,
    price: 0,
    priceRangeActive: false,
    priceRangeMin: 0,
    priceRangeMax: 0,
    discountActive: false,
    discount: 0,
    online: false,
    allowDeposit: false,
    depositPercentage: { value: null, disabled: true },
  };

  public constructor(
    private readonly fb: FormBuilder,
    private readonly serviceService: ServiceService,
    private readonly servicePhotoService: ServicePhotoService,
    private readonly toastr: ToastrService,
    private readonly transloco: TranslocoService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.form = this.fb.group({
      name: ['', Validators.required],
      description: [''],
      duration: [60, [Validators.required, Validators.min(5)]],
      price: [0, [Validators.required, Validators.min(0)]],
      priceRangeActive: [false],
      priceRangeMin: [0, [Validators.min(0)]],
      priceRangeMax: [0, [Validators.min(0)]],
      discountActive: [false, Validators.required],
      discount: [0, [Validators.min(0), Validators.max(100)]],
      online: [false],
      allowDeposit: [false],
      depositPercentage: [{value: null, disabled: true}, [Validators.required, Validators.min(1), Validators.max(99)]],
    });

    this.orderSub = this.orderChanged$
      .pipe(debounceTime(800))
      .subscribe(() => this.persistGalleryOrder());
  }

  public ngOnDestroy(): void {
    this.orderSub?.unsubscribe();
    this.queuedPhotos.forEach(p => URL.revokeObjectURL(p.previewUrl));
  }

  public ngOnChanges(changes: SimpleChanges) {
    if ((changes['service'] && this.service) || (changes['open'] && this.open && this.service)) {
      this.applyServiceToForm(this.service);
    }

    if (changes['open'] && this.open && !this.service) {
      this.resetFormForCreation();
    }
  }

  public onToggleOnline(online: boolean) {
    this.form.patchValue({ online: online });
  }

  public onImageUploaded(files: File[]) {
    if (!files || files.length !== 1) {
      return;
    }
    if (this.mainImagePreviewUrl) {
      URL.revokeObjectURL(this.mainImagePreviewUrl);
    }
    this.uploadedImage = files[0];
    this.mainImagePreviewUrl = URL.createObjectURL(files[0]);
  }

  public onGalleryBatchReady(batch: PreparedBatch) {
    if (!batch.files.length) {
      return;
    }

    const remainingSlots = this.maxGalleryPhotos - (this.galleryPhotos.length + this.queuedPhotos.length);
    if (remainingSlots <= 0) {
      this.toastr.info(
        this.transloco.translate('services.create-modal.photos.limit', { max: this.maxGalleryPhotos }),
        this.transloco.translate('services.create-modal.photos.title')
      );
      return;
    }
    const acceptedFiles = batch.files.slice(0, remainingSlots);
    if (acceptedFiles.length < batch.files.length) {
      this.toastr.info(
        this.transloco.translate('services.create-modal.photos.limit', { max: this.maxGalleryPhotos }),
        this.transloco.translate('services.create-modal.photos.title')
      );
    }

    if (this.service) {
      this.servicePhotoService.addPhotos(this.service.id, acceptedFiles).subscribe({
        next: (photos) => {
          this.galleryPhotos = [...this.galleryPhotos, ...photos].sort((a, b) => a.position - b.position);
          this.toastr.success(
            this.transloco.translate('services.create-modal.photos.add.success'),
            this.transloco.translate('services.create-modal.photos.title')
          );
        },
        error: () => {
          this.toastr.error(
            this.transloco.translate('services.create-modal.photos.add.error'),
            this.transloco.translate('services.create-modal.photos.title')
          );
        }
      });
    } else {
      const previews = acceptedFiles.map((file) => ({
        file,
        previewUrl: URL.createObjectURL(file),
      }));
      this.queuedPhotos = [...this.queuedPhotos, ...previews];
    }
  }

  public removeQueuedPhoto(photo: { file: File; previewUrl: string }) {
    URL.revokeObjectURL(photo.previewUrl);
    this.queuedPhotos = this.queuedPhotos.filter(p => p !== photo);
  }

  public deleteGalleryPhoto(photo: ServicePhoto) {
    if (!this.service) {
      return;
    }
    this.servicePhotoService.deletePhoto(this.service.id, photo.id).subscribe({
      next: () => {
        this.galleryPhotos = this.galleryPhotos.filter(p => p.id !== photo.id);
        this.toastr.success(
          this.transloco.translate('services.create-modal.photos.delete.success'),
          this.transloco.translate('services.create-modal.photos.title')
        );
      },
      error: () => {
        this.toastr.error(
          this.transloco.translate('services.create-modal.photos.delete.error'),
          this.transloco.translate('services.create-modal.photos.title')
        );
      }
    });
  }

  public dropPhoto(event: CdkDragDrop<ServicePhoto[]>) {
    moveItemInArray(this.galleryPhotos, event.previousIndex, event.currentIndex);
    this.updateLocalGalleryPositions();
    this.orderChanged$.next();
  }

  public onTogglePriceRange(priceRangeActive: boolean) {
    this.form.patchValue({ priceRangeActive: priceRangeActive });
  }

  public onToggleDiscountActive(discountActive: boolean) {
    this.form.patchValue({ discountActive: discountActive });
  }

  public onToggleDeposit(allowDeposit: boolean) {
    const depositControl = this.form.get('depositPercentage');
    this.form.patchValue({ allowDeposit });

    if (!depositControl) {
      return;
    }

    if (allowDeposit) {
      depositControl.enable();
      if (!depositControl.value) {
        depositControl.setValue(30);
      }
    } else {
      depositControl.setValue(null);
      depositControl.disable();
    }
  }

  public create() {
    if (this.form.invalid) {
      this.toastr.error(
        this.transloco.translate('services.create-modal.errors.fill-fields.content'),
        this.transloco.translate('services.create-modal.errors.fill-fields.title')
      );
      return;
    }

    this.creating = true;
    const {name, description, duration, price, priceRangeActive, priceRangeMin, priceRangeMax, discountActive, discount, online, allowDeposit, depositPercentage} = this.form.getRawValue();

    this.serviceService.createService(
      name,
      description,
      duration,
      price,
      priceRangeActive,
      priceRangeMin,
      priceRangeMax,
      discountActive,
      discount,
      online,
      allowDeposit,
      depositPercentage,
      [], // @TODO: Wire advantages later
      this.uploadedImage,
      this.queuedPhotos.map(p => p.file)
    ).subscribe({
      next: (service: Service) => {
        this.toastr.success(
          this.transloco.translate('services.create-modal.success.content'),
          this.transloco.translate('services.create-modal.success.content'),
        );
        this.creating = false;
        this.close(service);
      },
      error: () => {
        this.toastr.error(
          this.transloco.translate('services.create-modal.errors.failed-create.content'),
          this.transloco.translate('services.create-modal.errors.failed-create.title')
        );
        this.creating = false;
      }
    });
  }

  public edit() {
    if (!this.service) {
      return;
    }

    if (this.form.invalid) {
      this.toastr.error(
        this.transloco.translate('services.create-modal.errors.fill-fields.content'),
        this.transloco.translate('services.create-modal.errors.fill-fields.title')
      );
      return;
    }

    this.editing = true;
    const {name, description, duration, price, priceRangeActive, priceRangeMin, priceRangeMax, discountActive, discount, online, allowDeposit, depositPercentage} = this.form.getRawValue();

    this.serviceService.editService(
      this.service,
      name,
      description,
      duration,
      price,
      priceRangeActive,
      priceRangeMin,
      priceRangeMax,
      discountActive,
      discount,
      online,
      allowDeposit,
      depositPercentage,
      [], // @TODO: Wire advantages later
      this.uploadedImage,
      this.queuedPhotos.map(p => p.file)
    ).subscribe({
      next: (service: Service) => {
        this.galleryPhotos = (service.photos ?? []).sort((a, b) => a.position - b.position);
        this.toastr.success(
          this.transloco.translate('services.create-modal.edit.success.content'),
          this.transloco.translate('services.create-modal.edit.success.title'),
        );
        this.editing = false;
        this.close(service);
      },
      error: () => {
        this.toastr.error(
          this.transloco.translate('services.create-modal.edit.errors.failed-update.content'),
          this.transloco.translate('services.create-modal.edit.errors.failed-update.title')
        );
        this.editing = false;
      }
    });
  }

  public delete() {
    if (!isPlatformBrowser(this.platformId) || !this.service) {
      return;
    }

    // Show confirmation dialog

    const confirmed = window.confirm(
      this.transloco.translate('services.create-modal.danger-zone.confirmation.content')
    );

    if (!confirmed) {
      return; // If user cancels, exit the method
    }

    this.serviceService.deleteService(this.service).subscribe(
      () => {
        this.toastr.success(
          this.transloco.translate('services.create-modal.danger-zone.success.content'),
          this.transloco.translate('services.create-modal.danger-zone.success.title')
        );
        setTimeout(() => {
          this.uploadedImage = null;
          this.form.reset();
        },250);
        this.deleted.emit(this.service);
        this.close(null);
      }, () => {
        this.toastr.error(
          this.transloco.translate('services.create-modal.danger-zone.error.content'),
          this.transloco.translate('services.create-modal.danger-zone.error.title')
        );
      }
    )
  }

  public close(service: Service|null = null) {
    setTimeout(() => {
      this.resetFormForCreation(); // Prevent the reset to happen before the modal animation ends
    },250);
    this.closed.emit(service);
  }

  private syncDepositControlState() {
    const allowDeposit = !!this.form.get('allowDeposit')?.value;
    const depositControl = this.form.get('depositPercentage');

    if (!depositControl) {
      return;
    }

    if (allowDeposit) {
      depositControl.enable({ emitEvent: false });
    } else {
      depositControl.disable({ emitEvent: false });
    }
  }

  public toggleSection(section: keyof typeof this.sectionOpen): void {
    this.sectionOpen[section] = !this.sectionOpen[section];
  }

  private updateLocalGalleryPositions(): void {
    this.galleryPhotos = this.galleryPhotos.map((photo, index) => ({
      ...photo,
      position: index
    }));
  }

  private loadExistingPhotos(): void {
    if (!this.service) {
      this.galleryPhotos = [];
      return;
    }

    if (this.service.photos && this.service.photos.length > 0) {
      this.galleryPhotos = [...this.service.photos].sort((a, b) => a.position - b.position);
      return;
    }

    this.servicePhotoService.list(this.service.id).subscribe({
      next: (photos) => this.galleryPhotos = photos.sort((a, b) => a.position - b.position),
      error: () => {
        this.galleryPhotos = [];
      }
    });
  }

  private persistGalleryOrder(): void {
    if (!this.service || !this.galleryPhotos.length) {
      return;
    }

    const payload = this.galleryPhotos.map((photo, position) => ({
      id: photo.id,
      position
    }));

    this.servicePhotoService.updateOrder(this.service.id, payload).subscribe({
      next: () => {
        this.toastr.success(
          this.transloco.translate('services.create-modal.photos.order.success'),
          this.transloco.translate('services.create-modal.photos.title')
        );
      },
      error: () => {
        this.toastr.error(
          this.transloco.translate('services.create-modal.photos.order.error'),
          this.transloco.translate('services.create-modal.photos.title')
        );
      }
    });
  }

  private applyServiceToForm(service: Service): void {
    this.form.reset(this.getDefaultFormValue());
    this.form.patchValue({
      name: service.name,
      description: service.description,
      duration: service.duration,
      price: service.price,
      priceRangeActive: service.priceRangeActive,
      priceRangeMin: service.priceRangeMin,
      priceRangeMax: service.priceRangeMax,
      discountActive: service.discountActive,
      discount: service.discount,
      online: service.online,
      allowDeposit: service.allowDeposit,
      depositPercentage: service.allowDeposit ? service.depositPercentage : null,
    }, { emitEvent: false });
    this.syncDepositControlState();
    this.loadExistingPhotos();
    this.mainImagePreviewUrl = service.image ?? null;
    this.form.markAsPristine();
    this.form.markAsUntouched();
    this.form.updateValueAndValidity({ emitEvent: false });
  }

  private resetFormForCreation(): void {
    this.creating = false;
    this.editing = false;
    this.clearQueuedPreviews();
    this.galleryPhotos = [];
    this.mainImagePreviewUrl = null;
    this.uploadedImage = null;
    this.form.reset(this.getDefaultFormValue());
    this.syncDepositControlState();
    this.form.markAsPristine();
    this.form.markAsUntouched();
    this.form.updateValueAndValidity({ emitEvent: false });
  }

  private clearQueuedPreviews(): void {
    if (this.mainImagePreviewUrl && this.mainImagePreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.mainImagePreviewUrl);
    }
    this.queuedPhotos.forEach(p => URL.revokeObjectURL(p.previewUrl));
    this.queuedPhotos = [];
  }

  private getDefaultFormValue() {
    return {...this.defaultFormValues};
  }
}
